import React from "react";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import { useQuery } from "@apollo/react-hooks";
import { Button } from "reactstrap";

import Thread from "shared/react_components/Comments/Thread";
import MaterialIcon from "shared/react_components/MaterialIcon";
import LoaderOverlay from "shared/react_components/LoaderOverlay";

import NetworkStatus from "calls/components/NetworkStatus";
import { GetCommentThread } from "calls/components/graphql/Comments.graphql";
import { useOnSave, useOnDelete, useOnFollowingToggle, useUserMentionsData } from "./threadActions";
import { useRemoveCommentUrlParams } from "./removeCommentUrlParams";
import { blue10, blue70, viewAllThreadsButton, highlightedThreadNotFound } from "calls/styles/Comments.module.scss";

const HighlightedThread = ({ threadHashid, onAudioJumpPosition }) => {
  const location = useLocation();
  const urlSearchParams = new URLSearchParams(location.search);
  const removeCommentUrlParams = useRemoveCommentUrlParams();
  const commentHashid = urlSearchParams.get("comment_id");

  const onSave = useOnSave();
  const onDelete = useOnDelete();
  const onFollowingToggle = useOnFollowingToggle();
  const userMentionsData = useUserMentionsData();

  const { data, refetch, loading, networkStatus } = useQuery(
    GetCommentThread,
    {
      fetchPolicy: "cache-and-network",
      variables: { hashid: threadHashid },
      pollInterval: 15000
    }
  );
  const thread = data && data.commentThread;

  const onSaveForThread = async ({ hashid: commentId, richText, mentions }) => {
    await onSave({ threadId: thread.hashid, commentId, richText, mentions });
    await refetch();
  };

  const onCommentDelete = async (hashid) => {
    await onDelete({ threadId: thread.hashid, commentId: hashid });
    await refetch();
  };

  const onThreadFollowingToggle = async () => {
    await onFollowingToggle({ threadId: thread.hashid, following: thread.following });
    await refetch();
  };

  const isLoading = () => {
    return loading && networkStatus !== NetworkStatus.poll && networkStatus !== NetworkStatus.refetch;
  };

  return (
    <LoaderOverlay className="pt-4" loading={isLoading()}>
      <Button
        className={viewAllThreadsButton}
        onClick={removeCommentUrlParams}
        color="primary"
        style={{ backgroundColor: blue10, color: blue70, border: 0 }}
        data-test-id="show-all-threads-button"
      >
        <MaterialIcon name="chevron_left"/> View All Threads
      </Button>
      {thread && thread.comments && thread.comments.length > 0
        ? <Thread
          threadHashid={thread.hashid}
          following={thread.following}
          comments={thread.comments}
          onFollowingToggle={onThreadFollowingToggle}
          onDelete={onCommentDelete}
          onSave={onSaveForThread}
          highlightCommentHashid={commentHashid}
          onAudioJump={onAudioJumpPosition}
          userMentionsData={userMentionsData}
        />
        : <div
          className={highlightedThreadNotFound}
          style={{ visibility: isLoading() ? "hidden" : "visible" }}
          data-test-id="thread-not-found"
        >
          Thread not found
        </div>
      }
    </LoaderOverlay>
  );
};

HighlightedThread.propTypes = {
  threadHashid: PropTypes.string.isRequired,
  onAudioJumpPosition: PropTypes.func
};

export default HighlightedThread;
